import type { FormValues } from '@uturn/portal/modules/shipments/schema';
import {
	addValidationIssue,
	type ValidationResults,
	validationSuccess,
} from '@uturn/portal/types/validation';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

export const MINIMUM_REALISTIC_PRICE = 25;

export function usePriceValidation() {
	const { t } = useTranslation();
	/**
	 * price is required only during publishing.
	 *
	 * @param price
	 * @param publishing Publishing or updating published shipment
	 * @param validationResults
	 * @returns
	 */
	const validatePrice = (
		price: number,
		publishing: boolean,
		validationResults?: ValidationResults<FormValues>,
	): ValidationResults<FormValues> => {
		let results = validationResults ?? validationSuccess;

		if (!publishing) {
			return results;
		}

		const priceSchema = z
			.number({
				required_error: t(
					'general.errors.form_validation.required',
					'Required.',
				).toString(),
				invalid_type_error: t(
					'general.errors.form_validation.nan',
					'Must be a number.',
				).toString(),
			})
			.max(99999, {
				message: t(
					'general.errors.price.too_big',
					'This is too expensive.',
				).toString(),
			})
			.positive({
				message: t(
					'general.errors.form_validation.too_small',
					'Must be greater than 0.',
				).toString(),
			})
			.min(MINIMUM_REALISTIC_PRICE, {
				message: t(
					'formValidation:price_too_low',
					'Please enter a realistic target price.',
				).toString(),
			});
		try {
			priceSchema.parse(price);
		} catch (err) {
			if (err instanceof z.ZodError) {
				results = addValidationIssue<FormValues>(
					results,
					err.issues[0].message,
					'price.quantity',
				);
			}
		}
		return results;
	};
	return { validatePrice };
}
